import { DataGrid } from '@ibsys/kit';
import type { DataGridLeafColumn, DataGridSelectedCell } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomChartHeatmapPayload, IndicatorSelectHandler } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { chartNumberDigits, fmt } from '../../utils';
import { ChartCardShell } from '../ChartCardShell';
import { shownChartLabel } from '../chartValues';
import { chartElementBgClass, type ChartAppearance } from '../chartElements';
import { chartTableHeight, chartTableStyle } from '../chartLayout';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';

type Props = {
  title: string;
  payload: CustomChartHeatmapPayload;
  /** Пороги зон качества пользователя: ими окрашена ячейка со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Оформление диаграммы: правила раскраски ячеек по величине */
  appearance?: ChartAppearance;
  /**
   * Переход к диалогам ячейки; ячейка без разговоров остаётся некликабельной. Не задан — ячейки
   * не выбираются вовсе.
   */
  onSelect?: IndicatorSelectHandler;
  /** Высота диаграммы задана пользователем — карта прокручивает строки внутри неё */
  hasFixedHeight?: boolean;
};

/** Строка тепловой карты: подпись строки и значения по её столбцам. */
type HeatmapChartRow = {
  label: string;
  values: Record<string, number | null>;
};

/**
 * Ступени заливки ячейки по величине значения — от наименьшего к наибольшему.
 *
 * Шкала нейтральная (прозрачность основного цвета), а не зелёно-красная: величина
 * пользовательской диаграммы — любая (число разговоров, длительность), и «больше» здесь не
 * значит «лучше».
 */
const CELL_FILL_CLASSES = ['bg-primary/5', 'bg-primary/15', 'bg-primary/30', 'bg-primary/45', 'bg-primary/60'];

/** Ширина колонки подписи строки — под ФИО и названия значений атрибутов. */
const LABEL_COLUMN_SIZE = 180;

/** Ширина колонки значения. */
const VALUE_COLUMN_SIZE = 110;

/** Класс заливки ячейки по доле её значения от наибольшего в карте. */
const fillClass = (value: number | null, maxValue: number): string => {
  if (value === null || maxValue <= 0) return '';
  const step = Math.min(CELL_FILL_CLASSES.length - 1, Math.floor((value / maxValue) * CELL_FILL_CLASSES.length));
  return CELL_FILL_CLASSES[Math.max(0, step)];
};

/**
 * Тепловая карта «строка × столбец»: ячейки окрашены по величине значения, выбор ячейки
 * открывает список её диалогов.
 *
 * Заданные пользователем правила раскраски старше ступеней заливки: там, где они есть, ячейка
 * окрашена правилом, а что означает цвет, названо легендой под картой.
 *
 * С погашенным переходом ячейки не выбираются: выделение ячейки обещало бы переход, которого
 * в режиме правки состава нет.
 */
export const HeatmapChartView: FC<Props> = ({ title, payload, zoneRanges, appearance, onSelect, hasFixedHeight }) => {
  const { t } = useTranslation('qualityDashboard');

  const { rows, columns: columnLabels, cells } = payload;

  const maxValue = useMemo(() => cells.reduce((max, cell) => Math.max(max, cell.value ?? 0), 0), [cells]);
  // Ряд тепловой карты — все её ячейки: соседние столбцы написаны с одной точностью.
  const digits = useMemo(() => chartNumberDigits(cells.map((cell) => cell.value)), [cells]);

  const gridRows = useMemo((): HeatmapChartRow[] => {
    const byRow = new Map<string, Record<string, number | null>>();
    for (const cell of cells) {
      const rowValues = byRow.get(cell.row) ?? {};
      rowValues[cell.column] = cell.value;
      byRow.set(cell.row, rowValues);
    }
    return rows.map((label) => ({ label, values: byRow.get(label) ?? {} }));
  }, [rows, cells]);

  const columns = useMemo(
    (): DataGridLeafColumn<HeatmapChartRow>[] => [
      {
        id: 'label',
        header: t('presets.chart.heatmapRowColumn'),
        accessor: 'label',
        size: LABEL_COLUMN_SIZE,
        cell: ({ value }) => shownChartLabel(String(value)),
      },
      ...columnLabels.map(
        (columnLabel): DataGridLeafColumn<HeatmapChartRow> => ({
          id: columnLabel,
          header: shownChartLabel(columnLabel),
          accessor: (row) => row.values[columnLabel] ?? null,
          size: VALUE_COLUMN_SIZE,
          align: 'center',
          className: 'font-bold',
          cellClassName: ({ row }) => {
            const value = row.values[columnLabel] ?? null;
            // Заливка — во всю ячейку, без скругления: как у тепловой карты стандартного дашборда
            return chartElementBgClass({ value }, appearance, zoneRanges) ?? fillClass(value, maxValue);
          },
          cell: ({ value }) => fmt(value as number | null, digits),
        }),
      ),
    ],
    [t, columnLabels, maxValue, digits, appearance, zoneRanges],
  );

  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);

  const handleCellChange = (cell: DataGridSelectedCell | null) => {
    // Колонка подписи не ведёт к диалогам — за ней стоит вся строка, а не отдельная ячейка.
    if (!cell || cell.columnId === 'label') return;
    const selected = cells.find((item) => item.row === cell.rowId && item.column === cell.columnId);
    if (!selected) return;
    onSelect?.(
      {
        label: `${shownChartLabel(selected.row)} / ${shownChartLabel(selected.column)}`,
        conversationIds: selected.conversationIds,
        sourceColumns: selected.sourceColumns,
      },
      false,
    );
  };

  return (
    <ChartCardShell
      title={title}
      scrollable={false}
      footer={legendItems.length > 0 && <ChartLegend items={legendItems} />}
      hasFixedHeight={hasFixedHeight}
    >
      <DataGrid
        columns={columns}
        dataSource={{ mode: 'client', data: gridRows }}
        getRowId={(row) => row.label}
        enableColumnResizing
        enableCellSelection={!!onSelect}
        onSelectedCellChange={handleCellChange}
        height={chartTableHeight(gridRows.length, hasFixedHeight)}
        style={chartTableStyle(hasFixedHeight)}
      />
    </ChartCardShell>
  );
};
